import { computed, Directive, effect, ElementRef, inject, input, Renderer2, RendererStyleFlags2 } from '@angular/core';
import { Color, HEXColor } from '../models';
import { ColorService, isHexColor, isTailwindColor } from '../services/color.service';

/** Structural equality for GsColorBinding arrays — content-based so callers can pass new array literals without causing spurious DOM updates. */
function bindingsEqual(a: GsColorBinding[], b: GsColorBinding[]): boolean {
  return (
    a.length === b.length &&
    a.every((x, i) => x.color === b[i].color && x.aspect === b[i].aspect && x.state === b[i].state && x.opacity === b[i].opacity)
  );
}

/** CSS property a color binding targets, via the matching `*-custom` utility class. */
export type GsColorAspect = 'bg' | 'text' | 'border' | 'outline' | 'fill';

/** Interactive state a color binding applies to; empty string is the resting state. */
export type GsColorState = '' | 'hover' | 'focus' | 'focus-visible' | 'active' | 'disabled' | 'aria-disabled';

/** A single aspect/state/color assignment. */
export interface GsColorBinding {
  /** Tailwind token (e.g. `'primary-main'`) or HEX value (e.g. `'#ff0000'`). */
  color?: Color | string;
  /** Aspect to colorize. */
  aspect: GsColorAspect;
  /** State the color applies to; defaults to resting. */
  state?: GsColorState;
  /** Opacity in percent (0–100). */
  opacity?: number;
}

/**
 * Applies color bindings to the host, supporting Tailwind tokens and custom HEX alike.
 * Tokens become their class (`hover:bg-primary-main`); HEX values set a per-element CSS variable
 * (`--gs-bg-hover`) read by a static `*-custom` class — the only way to honor custom colors in
 * pseudo-states, which inline styles cannot express.
 */
@Directive({
  selector: '[gsColor]',
  standalone: true
})
export class GsColorDirective {
  // Services
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly renderer = inject(Renderer2);

  // Inputs & Models
  /** Color bindings to apply to the host. */
  readonly gsColor = input<GsColorBinding[]>([]);

  // Properties & Signals
  /** Stable view of the bindings — only notifies the effect when content actually changes, not on every new array reference. */
  private readonly stableBindings = computed(() => this.gsColor() ?? [], { equal: bindingsEqual });
  /** Classes applied to the host, tracked for cleanup on change. */
  private appliedClasses: string[] = [];
  /** CSS variables set on the host, tracked for cleanup on change. */
  private appliedVars: string[] = [];

  constructor() {
    effect(() => {
      const bindings = this.stableBindings();
      this.clear();
      const customAspects = this.aspectsWithCustomResting(bindings);
      bindings.forEach((binding) => this.apply(binding, customAspects.has(binding.aspect)));
    });
  }

  // Methods
  /** Removes the classes and CSS variables applied in the previous run. */
  private clear(): void {
    this.appliedClasses.forEach((cls) => this.renderer.removeClass(this.element, cls));
    this.appliedVars.forEach((name) => this.renderer.removeStyle(this.element, name, RendererStyleFlags2.DashCase));
    this.appliedClasses = [];
    this.appliedVars = [];
  }

  /**
   * Determines which aspects paint their resting color via a HEX-custom value.
   * The resting `*-custom` class is plain (unlayered) CSS, so it beats any layered Tailwind token
   * utility — including pseudo-state ones like `hover:bg-primary-main`. To keep hover/focus/etc.
   * applying, every state of such an aspect must go through the custom-var path too.
   * @param bindings The full set of bindings for the host.
   * @returns The aspects whose resting state uses a HEX color.
   */
  private aspectsWithCustomResting(bindings: GsColorBinding[]): Set<GsColorAspect> {
    const aspects = new Set<GsColorAspect>();
    bindings.forEach((binding) => {
      const state = binding.state ?? '';
      if (state === '' && binding.color && isHexColor(binding.color)) aspects.add(binding.aspect);
    });
    return aspects;
  }

  /**
   * Applies one binding to the host. Tailwind tokens normally become their utility class; HEX values —
   * and tokens when `forceCustom` is set — are routed through a `--gs-*` CSS variable read by the
   * matching `*-custom` class.
   * @param binding The color binding to apply.
   * @param forceCustom When true, Tailwind tokens are routed through the custom-var path (see {@link aspectsWithCustomResting}).
   */
  private apply(binding: GsColorBinding, forceCustom = false): void {
    const color = binding.color;
    if (!color) return;
    const state = binding.state ?? '';
    const prefix = state ? `${state}:` : '';

    if (isTailwindColor(color) && !forceCustom) {
      const opacitySuffix = binding.opacity != null ? `/${binding.opacity}` : '';
      this.addClass(`${prefix}${binding.aspect}-${color}${opacitySuffix}`);
    } else {
      const varName = `--gs-${binding.aspect}${state ? `-${state}` : ''}`;
      const value = isHexColor(color) ? this.resolveHex(color, binding.opacity) : this.resolveToken(color, binding.opacity);
      this.setVar(varName, value);
      this.addClass(`${prefix}${binding.aspect}-custom`);
    }
  }

  /**
   * Resolves a Tailwind token to a CSS value, applying opacity when given.
   * `@theme inline` runtime colors emit no `--color-*`, so they fall back to their `--gs-*` channel.
   * @param token The Tailwind color token (e.g. `'primary-main'`).
   * @param opacity Opacity in percent (0–100).
   * @returns The variable reference, wrapped in `color-mix` when an opacity is set.
   */
  private resolveToken(token: string, opacity?: number): string {
    const ref = `var(--color-${token}, rgb(var(--gs-${token})))`;
    return opacity == null ? ref : `color-mix(in srgb, ${ref} ${opacity}%, transparent)`;
  }

  /**
   * Resolves a HEX color to a CSS value, applying opacity when given.
   * @param hex The HEX color value (3-, 4-, 6- or 8-digit).
   * @param opacity Opacity in percent (0–100).
   * @returns The HEX value as-is, or an `rgb(… / a)` string combining its alpha with the opacity.
   */
  private resolveHex(hex: HEXColor, opacity?: number): string {
    if (opacity == null) return hex;
    const { r, g, b, a } = ColorService.hexToRgba(hex);
    return `rgb(${r} ${g} ${b} / ${a * (opacity / 100)})`;
  }

  /**
   * Adds a class to the host and tracks it for cleanup.
   * @param cls The class to add.
   */
  private addClass(cls: string): void {
    this.renderer.addClass(this.element, cls);
    this.appliedClasses.push(cls);
  }

  /**
   * Sets a CSS variable on the host and tracks it for cleanup.
   * @param name The variable name (including leading `--`).
   * @param value The value to assign.
   */
  private setVar(name: string, value: string): void {
    this.renderer.setStyle(this.element, name, value, RendererStyleFlags2.DashCase);
    this.appliedVars.push(name);
  }
}
